import { zodResolver } from '@hookform/resolvers/zod';
import { ProductDocument } from 'models/product';
import { useForm } from 'react-hook-form';
import { PlaceABidForm, placeABidFormSchema } from '../types';
import { getProductById } from '@util/firestore/products';
import { addressSchema } from 'models/address';
import { useQuery } from '@tanstack/react-query';
import { getPaymentMethods } from '@util/firestore/payments';
import { useAuth } from 'context/AuthContext';
import { getPublicUserDoc } from '@util/firestore/users';
import {
  createOffer,
  createOfferIntent,
  getOfferId,
} from '@util/firestore/offers';
import { getItem } from '@util/analytics';
import { OfferDocument, offerSchema } from 'models/offer';
import { getNumber } from '@util/createListingHelpers';
import { getHostUrl } from '@util/index';
import { logError } from '@util/logError';
import { logEvent } from '@util/analytics';
import { useStripe } from '@stripe/react-stripe-js';
import { useToastContext } from 'context/ToastContext';
import { createBid } from '@util/firestore/bid/bid.service';
import { BidDocument, bidSchema } from 'models/bid';

type usePlaceABidProps = {
  product: ProductDocument;
  onSubmitSuccess: () => void;
};

export const usePlaceABid = ({
  product,
  onSubmitSuccess,
}: usePlaceABidProps) => {
  const { userDoc } = useAuth();
  const stripe = useStripe();
  const { showSuccessToast } = useToastContext();
  const useFormReturn = useForm<PlaceABidForm>({
    resolver: zodResolver(placeABidFormSchema),
  });
  const { data: paymentMethodData } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
    enabled: !!userDoc,
  });
  async function onSubmit(data: PlaceABidForm) {
    if (!userDoc || !stripe) return;
    const now = Date.now();
    const currProduct = await getProductById(product.id);
    if (!currProduct || currProduct.out_of_stock) {
      console.warn('Product is out of stock');
      useFormReturn.setError('root', {
        message: 'Product is out of stock',
      });
      return;
    }
    if (currProduct.end_time! < now) {
      console.warn('Auction has ended');
      useFormReturn.setError('root', {
        message: 'Auction has ended',
      });
      return;
    }
    const shippingAddressObj = JSON.parse(data.shippingAddress);
    shippingAddressObj.address_line2 = shippingAddressObj.address_line2 ?? '';
    const shippingAddress = addressSchema.parse(shippingAddressObj);
    const paymentMethod = paymentMethodData?.find(
      (paymentMethod) => paymentMethod.id === data.paymentMethodId
    );
    if (!paymentMethod?.id) {
      useFormReturn.setError('root', {
        message: 'Please select a payment method',
      });
      return;
    }
    const offer: OfferDocument = {
      id: getOfferId(),
      is_counter: false,
      is_exclusive: false,
      attribution: {},
      buyer_id: userDoc.uid,
      seller_id: currProduct.seller_id,
      product_id: currProduct.id,
      current_price: currProduct.price,
      account_id: '', // added in backend
      customer_id: userDoc.customer_id,
      is_auction: true,
      price: getNumber(data.price),
      shipping_cost: data.shippingCost,
      total: data.shippingCost + getNumber(data.price) + data.tax,
      message: '',
      address: shippingAddress,
      payment: paymentMethod,
      uids: [userDoc.uid, currProduct.seller_id],
      created: now,
      from_web: true,
      state: 0,
      tax: data.tax,
    };

    const offerIntent = await createOfferIntent(offer.price, offer.customer_id);
    const { error } = await stripe.confirmCardSetup(offerIntent.data, {
      payment_method: paymentMethod.id,
    });
    if (error) {
      useFormReturn.setError(
        'root',
        {
          message:
            'Error confirming payment method. Please refresh the page and try again.',
        },
        { shouldFocus: true }
      );
      return;
    }
    try {
      const safeParsedOffer = offerSchema.safeParse(offer);
      if (!safeParsedOffer.success) {
        logError(new Error('Error parsing offer'));
        useFormReturn.setError('root', {
          message: 'Error parsing offer',
        });
        return;
      }
      const parsedOffer = safeParsedOffer.data;
      await createOffer(parsedOffer);
      logEvent(
        'created_offer',
        {
          items: [getItem(currProduct)],
          offer_id: parsedOffer.id,
        },
        userDoc.uid
      );
      const bid: BidDocument = {
        username:
          userDoc.username.charAt(0) +
          '***' +
          userDoc.username.charAt(userDoc.username.length - 1),
        uid: parsedOffer.buyer_id,
        product_id: parsedOffer.product_id,
        offer_id: parsedOffer.id,
        bid_price: parsedOffer.price,
        created: now,
        auto: false,
        ...(data.maxPrice && { max_bid: data.maxPrice }),
      };
      const safeParsedBid = bidSchema.safeParse(bid);
      if (!safeParsedBid.success) {
        logError(new Error('Error parsing bid'));
        useFormReturn.setError('root', {
          message: 'Error parsing bid',
        });
        return;
      }
      const parsedBid = safeParsedBid.data;
      await createBid(parsedBid);
      showSuccessToast('Placed bid successfully');
      onSubmitSuccess();
    } catch (error) {
      logError(error);
      useFormReturn.setError(
        'root',
        {
          message: 'Error creating offer. Please contact support.',
        },
        { shouldFocus: true }
      );
      return;
    }
  }

  return {
    useFormReturn,
    onSubmit,
  };
};
